import { animate, group, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';

import { translate } from '@jsverse/transloco';
import { Subject, debounceTime, takeUntil, tap } from 'rxjs';
import { DropdownObject, EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { DatasourceManagerStore } from 'app/modules/dashboard/stores/datasource-manager.store';
import {
  Dashboard,
  DashboardService,
  DashboardType,
  DashboardVisibility,
} from 'app/providers/bridge-api/dashboard.service';
import { Template } from 'app/providers/dashboard-templates.service';
import { Analysis } from 'app/providers/storage-api/analysis.service';
import { Project } from 'app/providers/storage-api/project.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { WidgetDataSource } from '../../../../models';
import { generalAsyncNameValidator } from '../../../../utils/customValidators';
import { ProjectCollection } from '../../models/project-collection.model';
import { TemplateService } from '../../services/template.service';
import { createInitializer } from './dashboard-initializer';

interface DashboardTypeDropdownObject extends DropdownObject {
  description: string;
  template?: Template;
}

export interface NewDashboardData {
  project: Project;
  timezone: string;
  timezoneOffset: string;
  saveAsDashboard?: Dashboard;
  title?: string;
  isTemplate?: boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-new-dashboard',
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          'max-height': '100px',
          opacity: '1',
          visibility: 'visible',
        })
      ),
      state(
        'closed',
        style({
          'max-height': '100px',
          opacity: '0',
          visibility: 'hidden',
        })
      ),
      transition('open => closed', [
        group([
          animate(
            '1ms ease-out',
            style({
              visibility: 'hidden',
            })
          ),
          animate(
            '250ms ease-out',
            style({
              opacity: '0',
            })
          ),
        ]),
      ]),
      transition('closed => open', [
        group([
          animate(
            '1ms ease-in',
            style({
              visibility: 'visible',
            })
          ),
          animate(
            '250ms ease-in',
            style({
              opacity: '1',
            })
          ),
        ]),
      ]),
    ]),
  ],
  templateUrl: './new-dashboard.component.html',
  styleUrls: ['./new-dashboard.component.scss'],
  standalone: false,
})
export class NewDashboardComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() data: NewDashboardData;
  @Output() modalClosed: EventEmitter<any> = new EventEmitter();

  project: Project;
  saveAsMode = false;
  saveAsDashboard: Dashboard;
  title = translate('new-dashboard.create-dashboard');
  isTemplate: boolean;
  collections: ProjectCollection[] = [];

  isLoading = {
    save: false,
    validation: false,
    init: false,
  };
  formState = {
    isValid: false,
    message: '',
  };

  timezone: string;
  timezoneOffset: string;

  selectedCollectionName: string;
  selectedCollection: ProjectCollection = null;
  selectedAnalysisName: string;
  selectedAnalysis: Analysis = null;
  dashboardName = '';
  note: string;
  sentimentScale = 3;
  sentimentDisplay = 'polarity';
  numberOfColumns = 12;
  isExecutive = false;
  visibility: DashboardVisibility = 'PUBLIC';

  dashboardNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS, text: '' };

  dashboardTypeOptions: DashboardTypeDropdownObject[] = [
    {
      title: translate('new-dashboard.traditional-title'),
      id: 'traditional',
      description: translate('new-dashboard.traditional-description'),
    },
  ];
  selectedDashboardTypeOption = this.dashboardTypeOptions[0];
  dashboardTypeTooltip;
  readonly traditionalDashboardTypeValue: DashboardType = 'traditional';

  dashboardForDataSource;
  selectedDataSource: WidgetDataSource;

  nameValidator$ = new Subject();
  unsub$ = new Subject<void>();

  private dashboardService = inject(DashboardService);
  private readonly _templateService = inject(TemplateService);
  readonly datasourceManagerStore = inject(DatasourceManagerStore);

  constructor() {}

  async ngOnInit(): Promise<void> {
    this.isLoading.init = true;

    this.project = this.data.project;
    this.timezone = this.data.timezone;
    this.timezoneOffset = this.data.timezoneOffset;
    this.saveAsDashboard = this.data.saveAsDashboard;
    this.saveAsMode = !!this.data.saveAsDashboard;
    this.title = this.data.title || this.title;
    this.isTemplate = this.data.isTemplate;
    if (this.saveAsMode) {
      this.note = this.saveAsDashboard.payload.notes;
      this.sentimentScale = parseInt(this.saveAsDashboard.payload.sentimentScale.toString(), 10);
      this.sentimentDisplay = this.saveAsDashboard.payload.sentimentDisplay;
      this.isExecutive = this.saveAsDashboard.payload.isExecutive;
      this.visibility = this.saveAsDashboard.visibility;
      this.dashboardTypeTooltip = { text: translate('new-dashboard.dashboard-type-cannot-change') };
    }

    this.nameValidator$
      .pipe(
        takeUntil(this.unsub$),
        tap(() => (this.isLoading.validation = true)),
        debounceTime(500)
      )
      .subscribe(() => {
        generalAsyncNameValidator(
          this.dashboardName,
          this.dashboardNameStatus,
          translate('new-dashboard.dashboard'),
          64,
          true,
          () => this.dashboardService.validateDashboardName(this.dashboardName, this.project.id)
        )
          .then((res) => (this.dashboardNameStatus = { ...res }))
          .finally(() => {
            this.isLoading.validation = false;
            this.formState = this.validate();
          });
      });
    const latestAnalyses: Analysis[] = [];
    this.collections = ProjectCollection.createListFromProject(this.project);
    this.collections.forEach((c) => {
      if (c.data.analysisCount) {
        latestAnalyses.push(c.analyses[0]);
      }
    });

    if (latestAnalyses.length) {
      latestAnalyses.sort((a, b) => +b.createdAt - +a.createdAt);
      const latestCollection = this.collections.find((c) => c.analyses.includes(latestAnalyses[0]));
      this.selectedCollectionName = latestCollection.data.collectionName;
      this.onCollectionSelected(latestCollection);

      this.selectedAnalysisName = latestCollection.analyses[0].displayName;
      this.onAnalysisSelected(latestCollection.analyses[0]);
    } else {
      this.selectedCollectionName = this.collections[0].data.collectionName;
      this.onCollectionSelected(this.collections[0]);
    }

    await this.setupDashboardTypes(latestAnalyses);

    this.isLoading.init = false;
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  public cancel(): void {
    this.modalClosed.emit(null);
  }

  public onCollectionSelected(collection: ProjectCollection) {
    this.selectedCollection = collection;
    if (collection.data.analysisCount === 1) {
      this.selectedAnalysisName = collection.analyses[0].displayName;
      this.onAnalysisSelected(collection.analyses[0]);
    } else {
      this.selectedAnalysisName = '';
      this.selectedAnalysis = undefined;
    }
  }

  public onAnalysisSelected(analysis: Analysis) {
    this.selectedAnalysis = analysis;
    this.selectedAnalysisName = analysis.displayName;
  }

  onDataSourceSelected($event) {
    this.selectedDataSource = $event;

    let collectionName = this.selectedDataSource.collectionName;
    let analysisId = this.selectedDataSource.analysisId;
    if (this.selectedDataSource.groupId) {
      collectionName = this.selectedDataSource.groupCollections[0];
      analysisId = this.selectedDataSource.groupAnalyses[0];
    }

    this.onCollectionSelected(
      this.collections.find((c) => c.data.collectionName === collectionName)
    );
    this.onAnalysisSelected(this.selectedCollection.analyses.find((a) => a.name === analysisId));
    this.formState = this.validate();
  }

  public onDashboardTypeChange(dashboardType) {
    this.selectedDashboardTypeOption = dashboardType;
    this.dashboardForDataSource = {
      payload: { type: this.selectedDashboardTypeOption.id as DashboardType },
    };
    this.selectedDataSource = undefined;
    this.formState = this.validate();
  }

  validate() {
    if (this.dashboardNameStatus.state) {
      return {
        isValid: false,
        message: this.dashboardNameStatus.text,
      };
    }
    if (this.isTemplate && !this.selectedDataSource) {
      return {
        isValid: false,
        message: translate('new-dashboard.select-data-source'),
      };
    }
    if (
      !this.saveAsMode &&
      this.selectedDashboardTypeOption.id !== this.traditionalDashboardTypeValue &&
      !this.selectedDataSource
    ) {
      return {
        isValid: false,
        message: translate('new-dashboard.select-data-source'),
      };
    }
    return {
      isValid: true,
      message: undefined,
    };
  }

  public canCreateDashboard(): boolean {
    if (!this.formState.isValid) {
      return false;
    }
    if (this.isLoading.save) {
      return false;
    }
    if (this.isLoading.validation) {
      return false;
    }
    return true;
  }

  public createDashboard() {
    this.isLoading.save = true;

    let dashboard: Partial<Dashboard> = {
      title: this.dashboardName.trim(),
      visibility: this.visibility,
      widgets: [],
      widgetGroups: {},
      comparisons: {},
      properties: {
        is_locked: false,
      },
      payload: {
        collectionName: this.selectedCollectionName,
        analysisName: this.selectedAnalysisName === undefined ? '' : this.selectedAnalysisName,
        notes: this.note,
        //Even though TS thinks sentimentScale is a number, it is converted to a string by the radio at runtime
        sentimentScale: parseInt(this.sentimentScale.toString(), 10),
        sentimentDisplay: this.sentimentDisplay,
        type: this.selectedDashboardTypeOption.id as DashboardType,
        pages: [{ title: translate('new-dashboard.page-one'), id: 1 }],
        timezone: this.timezone,
        timezoneOffset: this.timezoneOffset,
        lastDataSourceId: this.selectedDataSource?.id,
        actionPlans: [],
        pins: [],
        isExecutive: this.isExecutive,
      },
    };

    if (this.saveAsMode) {
      dashboard.payload.pages = JSON.parse(JSON.stringify(this.saveAsDashboard.payload.pages));
      dashboard.widgets = JSON.parse(JSON.stringify(this.saveAsDashboard.widgets));
      dashboard.widgetGroups = JSON.parse(JSON.stringify(this.saveAsDashboard.widgetGroups));
      dashboard.comparisons = JSON.parse(JSON.stringify(this.saveAsDashboard.comparisons));

      if (this.isTemplate) {
        for (const widget of dashboard.widgets) {
          widget.dataSourceId = this.selectedDataSource.id;
          widget.payload.projectId = this.project.id;
        }
      }

      this.dashboardService.createDashboard(dashboard, this.project.id).then((result) => {
        this.modalClosed.emit(result);
      });

      return;
    }

    const dashboardInitializer = createInitializer(
      dashboard.payload.type,
      this.project.id,
      this.selectedDataSource?.id,
      this.selectedDashboardTypeOption.template
    );
    dashboard = dashboardInitializer.initialize(dashboard);
    this.modalClosed.emit(dashboard);
  }

  private async setupDashboardTypes(latestAnalyses: Analysis[]): Promise<void> {
    const surveyTypeOption: DashboardTypeDropdownObject = {
      title: translate('new-dashboard.survey-title'),
      id: 'survey',
      description: translate('new-dashboard.survey-description'),
    };
    this.dashboardTypeOptions.push(surveyTypeOption);

    if (this.saveAsMode) {
      this.selectedDashboardTypeOption = this.dashboardTypeOptions.find(
        (o) => o.id === (this.saveAsDashboard.payload.type || 'traditional')
      );
    }

    if (!this.saveAsMode) {
      if (!latestAnalyses.some((a) => a.hasTaxonomy)) {
        surveyTypeOption.disabled = true;
        surveyTypeOption.subtitle = translate('new-dashboard.survey-hint');
      }
    }

    const templates = await this._templateService.list();
    this.dashboardTypeOptions.push(
      ...templates.map((t) => ({
        title: t.dashboardType,
        id: t.dashboardType,
        description: t.description,
        template: t,
      }))
    );
  }
}
